// Turning a feature on for some of the people, some of the time.
//
// A rollout is a *fraction*, and the whole difficulty is that the fraction has
// to be stable: a player who reloads and finds the feature gone has not been
// shown the feature, they have been shown a bug. So membership is a pure
// function of who is asking and which flag is being asked about — no dice, no
// server round trip per read, and the same answer on every render.
//
// ## The one hard rule
//
// **Nothing in `src/engine/rules/` may read a flag, and `eslint.config.js` enforces
// it.** Two networked clients derive one board from one command log, so a rule
// that consulted a flag would derive *two different games* from one log — the
// desync this codebase's determinism rules exist to prevent, arriving through
// a door marked "product". It is the same argument the hint profile carries
// (`telemetry/profile.ts`): a player editing their own `localStorage` must
// change nothing but their own screen, and the test for "have I put this in
// the wrong place" is whether a lie here could affect anybody else's game.
//
// So flags gate *presentation and pacing*: what a screen looks like, which
// copy is shown, how a driver spends time. Never what a move is worth, what a
// command does, or anything that reaches the reducer.

/** A flag, and what turning it on is meant to answer. */
export interface Flag {
  id: string;
  /** What it does, in the shortest sentence that would settle an argument. */
  detail: string;
  /**
   * The share of visitors it is on for, 0 to 1, when nothing overrides it.
   *
   * A *default* rather than the setting: the deployment's `FLAGS` variable is
   * what actually decides, so a rollout can be widened or pulled without a
   * rebuild. This is what a laptop with no variable set sees.
   */
  rollout: number;
  /**
   * The arms, for a flag that is more than on and off.
   *
   * Split evenly across `rollout`, so two arms at a rollout of 0.6 are 30% and
   * 30% with the remaining 40% on the control — one number still turns the
   * whole experiment off, which is what keeps `FLAGS=<id>=0` the way to stop
   * anything in a hurry.
   *
   * **Evenly on purpose**: unequal arms are a thing to want eventually and a
   * thing to get wrong now, since an experiment whose arms are different sizes
   * is one whose results have to be weighted before they can be compared.
   *
   * The values are the flag's own vocabulary — turns, pixels, a name. Absent
   * means a plain boolean, which is what `isOn` answers about.
   */
  variants?: readonly (string | number)[];
}

/**
 * Every flag there is.
 *
 * **A table rather than free strings**, for `MECHANICS`' reason: a typo in a
 * flag id is a feature that is off for everybody and looks exactly like a
 * feature that is not finished. `FlagId` is the union, so a misspelling is a
 * type error rather than a silent nought.
 *
 * Flags are meant to be *deleted*. One that has reached 1 and stayed there is
 * a branch nobody is choosing between any more, and the tidy-up is to inline
 * the winner and drop the row.
 */
export const FLAGS = [
  {
    id: "score-panel",
    detail:
      "The running capture score in a run's top-right corner. The first thing " +
      "shipped through this, and the reason it is a presentation flag: it " +
      "reads the tally the rules already keep and changes nothing about play.",
    rollout: 1
  },
  {
    id: "require-account",
    detail:
      "The door: a visitor who is not signed in and approved sees the landing " +
      "page instead of the board (§25). **At 1 on purpose, and not an " +
      "experiment** — invite-only is the decision rather than one arm of a " +
      "trial, so there is nothing here to choose between and no ramp to walk " +
      "up. What it stays for is stopping the landing page being *drawn* while " +
      "something is wrong with it. " +
      "**It is not the way to reopen the board.** The server gate is a "  +
      "separate question and does not read this: unset GOOGLE_CLIENT_ID on " +
      "the deployment and accountsRequired() goes false, which is the real " +
      "switch and is also a variable change. " +
      "**It decides what the client draws and nothing else.** The server " +
      "enforces access on its own through `unlocked(req)`, and must never read " +
      "this flag: `?flag=-require-account` is a URL anybody can type, so a " +
      "server that honoured it would have no gate at all. That is the one " +
      "place this table's usual promise — that a forced flag only ever shows " +
      "its holder a screen — stops being free, and showing the board is " +
      "exactly what it is wanted for during testing.",
    rollout: 1
  },
  {
    id: "wave-delay",
    detail:
      "Turns of breathing room between clearing a front and the next camp " +
      "landing. Two arms — one turn and five — against today's game, where " +
      "the camp arrives in the same beat that sweeps the last one.",
    rollout: 0.6,
    variants: [1, 5]
  },
  // The ruleset dial (§26 F1, `src/engine/rules/ruleset.ts`). Its only job is
  // to choose the *default* for a new visitor — the value still rides in the
  // opening command and replays like any other setup, so a rollout here
  // decides what a fresh game opens on and can never reach into one already
  // being played. At 0: today's game is the default.
  {
    id: "ruleset-spawn-clock",
    detail:
      "New runs spawn camps on a clock instead of on a beaten front. Inert " +
      "until F10 builds the clock.",
    rollout: 0
  }
] as const satisfies readonly Flag[];

export type FlagId = (typeof FLAGS)[number]["id"];

/** The rollout a flag ships with, before a deployment overrides it. */
export const flagById = (id: FlagId): Flag | undefined =>
  FLAGS.find((flag) => flag.id === id);

/**
 * Where in the rollout this visitor falls for this flag, in `[0, 1)`.
 *
 * **Salted with the flag id, which is the half that is easy to leave out.**
 * Hashing the key alone puts every visitor at the same point in *every*
 * rollout: the first 10% of one flag would be exactly the first 10% of the
 * next, so a handful of unlucky people would get every experiment at once and
 * nobody else would get any. Independent buckets per flag is what makes two
 * rollouts two samples.
 *
 * FNV-1a, because it is short, has no dependencies and is not a security
 * boundary — nothing here is secret and a visitor may lie about their own key
 * without affecting anybody else, which is the same licence the hint profile
 * takes.
 */
export function bucketOf(flag: string, key: string): number {
  let hash = 0x811c9dc5;
  const text = `${flag}:${key}`;
  for (let i = 0; i < text.length; i++) {
    hash ^= text.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash / 0x100000000;
}

/**
 * Is this flag on for this visitor?
 *
 * **A strict `<`, so a rollout of 0 is off for everybody.** With `<=` a
 * visitor who hashed to exactly nought would see a feature nobody had rolled
 * out, which is the one value that has to mean "nobody".
 *
 * A rollout of 1 is on for everybody: `bucketOf` cannot return 1, since the
 * hash is divided by a number one larger than its own maximum.
 */
export const isOn = (flag: string, key: string, rollout: number): boolean =>
  rollout > 0 && bucketOf(flag, key) < rollout;

/**
 * Which of several variants this visitor gets, from one bucket.
 *
 * **One bucket and disjoint bands, never two flags.** The obvious way to run a
 * three-way test is two boolean flags at 30% each — and because buckets are
 * salted per flag they are *independent*, so about 9% of visitors come out in
 * both and there is no honest answer to what those people were shown. Bands
 * over a single bucket cannot overlap by construction: a visitor has one
 * position, and it falls in exactly one band.
 *
 * Shares are taken in order and need not sum to 1; whatever is left over is the
 * control, which is what `fallback` names. So `[[0.3, "a"], [0.3, "b"]]` is
 * 30/30/40 and reads as the thing it is.
 */
export function variantOf<T, F = T>(
  flag: string,
  key: string,
  bands: readonly (readonly [share: number, value: T])[],
  fallback: F
): T | F {
  const at = bucketOf(flag, key);
  let edge = 0;
  for (const [share, value] of bands) {
    if (share <= 0) continue;
    edge += share;
    if (at < edge) return value;
  }
  return fallback;
}

/**
 * A flag's arms as disjoint bands, sized by the rollout it is running at.
 *
 * Evenly split, so the rollout stays one number meaning "how much of the
 * audience is in this experiment" whether the flag has two outcomes or four.
 */
export const bandsFor = <T>(
  rollout: number,
  values: readonly T[]
): readonly (readonly [number, T])[] =>
  values.map((value) => [rollout / values.length, value] as const);

/**
 * The rollouts a deployment is running, from its `FLAGS` variable.
 *
 * `"score-panel=0.25,new-camera=1"` — a share per flag, comma separated.
 * Anything absent keeps the table's own default, so the variable names only
 * what is being *changed*, and an empty or unset variable is the shipped
 * configuration rather than everything off.
 *
 * **Never throws, and an unreadable entry is dropped rather than defaulted.**
 * This is parsed from a string somebody typed into a dashboard at the moment
 * they wanted a feature turned off in a hurry; taking the whole deployment
 * down over a stray comma would be the worst possible time for it. A
 * percentage is accepted too, because `25` is what half the world will type.
 */
export function parseRollouts(spec: string | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  for (const entry of (spec ?? "").split(",")) {
    const at = entry.indexOf("=");
    if (at < 0) continue;
    const id = entry.slice(0, at).trim();
    const text = entry.slice(at + 1).trim();
    // **An empty value is dropped, and `Number("")` is the trap.** It answers
    // *nought*, not `NaN`, so `score-panel=` would have read as "off for
    // everybody" — a typo silently retiring a feature, which is the one
    // failure this parser must not have. An operator who means off types a 0.
    const raw = Number(text);
    if (!id || text === "" || !Number.isFinite(raw) || raw < 0) continue;
    // `25` means a quarter and `0.25` means a quarter. Nobody who types the
    // first means twenty-five times everybody.
    const share = raw > 1 ? raw / 100 : raw;
    out[id] = Math.min(1, share);
  }
  return out;
}

/** Every flag's rollout, the deployment's answer over the table's. */
export function rolloutsFor(spec: string | undefined): Record<string, number> {
  const override = parseRollouts(spec);
  const out: Record<string, number> = {};
  for (const flag of FLAGS) out[flag.id] = override[flag.id] ?? flag.rollout;
  return out;
}
